import dynamic from 'next/dynamic';
import { MapPin } from '@phosphor-icons/react';
import {
  Input,
  Box,
  FormLabel,
  VisuallyHidden,
  Text,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Button,
} from '@chakra-ui/react';
import {
  useAppdata,
  useMembershipInfo,
  useTranslation,
} from '../lib/hooks.context';
import { Option, AutoSuggestProps } from './AutoSuggest';
import { Site } from '../types';

const AutoSuggest = dynamic(() => import('./AutoSuggest'));

interface Props {
  onAdd: AutoSuggestProps['onAdd'];
  isLoading?: boolean;
  autoFocus?: boolean;
  placeholder?: string | null;
}

type SiteOption = Omit<Option, 'href' | 'target'>;

export type { SiteOption };

const mapSitesToOptions = (sites: Site[]): SiteOption[] =>
  sites.reduce(
    (acc, { blogname: title, name, blog_id: id, tags }) =>
      !name
        ? acc
        : [
            ...acc,
            {
              node: () => (
                <Text as="span" py="2" px="1">
                  {title}
                </Text>
              ),
              name: title,
              value: name,
              tags,
              id,
            },
          ],
    [],
  );

const SiteSelect = ({
  onAdd,
  isLoading,
  autoFocus = true,
  placeholder = null,
}: Props) => {
  const { sites, subsite } = useAppdata();
  const { membershipSite } = useMembershipInfo();
  const t = useTranslation();

  if (!sites) {
    return null;
  }

  const items = mapSitesToOptions(sites);

  const label = t('site_select_label');

  const currentSiteName =
    (membershipSite || subsite
      ? items.find(({ value }) => value === (membershipSite || subsite))?.name
      : '') || t('site_select_placeholder');

  return (
    <AutoSuggest {...{ items, onAdd }} initialItems={items} preventMultiselect>
      {({
        getOuterProps,
        getInputProps,
        openMenu,
        selectedItems,
        getLabelProps,
      }) => (
        <Box {...getOuterProps()}>
          <FormLabel {...getLabelProps()} as={VisuallyHidden}>
            {label}
          </FormLabel>
          <InputGroup size="normal">
            <InputLeftElement pointerEvents="none" color="highlightColor">
              <MapPin />
            </InputLeftElement>
            <Input
              placeholder={placeholder || currentSiteName}
              variant="search"
              {...getInputProps({
                onFocus: openMenu,
              })}
              autoFocus={autoFocus}
              {...(selectedItems?.length
                ? { value: selectedItems[0].value }
                : null)}
            />
            {!isLoading ? null : (
              <InputRightElement>
                <Button
                  pointerEvents="none"
                  as="span"
                  variant="transparent"
                  width="1em"
                  isLoading
                />
              </InputRightElement>
            )}
          </InputGroup>
        </Box>
      )}
    </AutoSuggest>
  );
};

export default SiteSelect;
